import {Image, Platform, Pressable, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'

import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {Text} from '#/components/Typography'
import type {StudioVideo} from '#/lib/api/studio'
import {formatViewCount} from '#/lib/strings/view-count'
import {Pencil_Stroke2_Corner0_Rounded as PencilIcon} from '#/components/icons/Pencil'

export function StudioVideoListItem({
  video,
  onEdit,
}: {
  video: StudioVideo
  onEdit?: (video: StudioVideo) => void
}) {
  const {_} = useLingui()
  const t = useTheme()
  const navigation = useNavigation()

  const statusLabel =
    video.status === 'published'
      ? _(msg`Published`)
      : video.status === 'processing'
        ? _(msg`Processing`)
        : video.status === 'uploading'
          ? _(msg`Uploading`)
          : _(msg`Draft`)

  const onPress = () =>
    navigation.navigate('StudioVideo', {videoId: video.id} as const)

  return (
    <View
      style={[
        a.flex_row,
        a.gap_md,
        a.p_lg,
        a.border_t,
        t.atoms.border_contrast_low,
        t.atoms.bg,
        a.align_center,
      ]}>
      <Pressable
        onPress={onPress}
        style={[a.flex_1, a.flex_row, a.gap_md, Platform.OS === 'web' && {cursor: 'pointer'}]}
        accessibilityRole="button"
        accessibilityLabel={_(msg`Open video`)}>
        <View
        style={[
          a.rounded_md,
          t.atoms.bg_contrast_25,
          {width: 160, height: 90, overflow: 'hidden'},
        ]}>
        {video.thumbnail_path ? (
          Platform.OS === 'web' ? (
            <img
              src={video.thumbnail_path}
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                cursor: 'pointer',
              }}
            />
          ) : (
            <Image
              source={{uri: video.thumbnail_path}}
              style={{width: '100%', height: '100%'}}
              resizeMode="cover"
            />
          )
        ) : (
          <View style={[a.flex_1, a.align_center, a.justify_center]}>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              <Trans>No thumbnail</Trans>
            </Text>
          </View>
        )}
      </View>
      <View style={[a.flex_1, a.justify_center]}>
        <Text
          style={[a.text_md, a.font_semi_bold, t.atoms.text]}
          numberOfLines={2}>
          {video.title || _(msg`Untitled`)}
        </Text>
        <Text
          style={[a.text_sm, t.atoms.text_contrast_medium]}
          numberOfLines={1}>
          {statusLabel} · {formatViewCount(video.total_views)} <Trans>views</Trans>
        </Text>
        {video.description ? (
          <Text
            style={[a.text_xs, t.atoms.text_contrast_low]}
            numberOfLines={2}>
            {video.description}
          </Text>
        ) : null}
      </View>
      </Pressable>
      {onEdit && (
        <Button
          label={_(msg`Edit video`)}
          size="small"
          color="secondary"
          variant="outline"
          onPress={() => onEdit(video)}>
          <ButtonIcon icon={PencilIcon} />
          <ButtonText>
            <Trans>Edit</Trans>
          </ButtonText>
        </Button>
      )}
    </View>
  )
}
